import { Injector, NgZone } from '@angular/core';
import { ToastService } from '../../../core/services/toast.service';
import { PosStateService } from '../pos-state.service';

/**
 * An alert, with when it came in: its toast stays up for `toastMs`, and a
 * held one is dropped from the hold queue at `maxAgeMs` old.
 */
export interface LockAwareAlert {
  key: string;
  message: string;
  since: number;
  toastMs: number;
  maxAgeMs: number;
}

/**
 * The error toasts of a root-level POS alert service, on a terminal that may
 * be locked: OFP-093's print failures (PrintFailedAlertsService) and TCPAY-1's
 * TableConnect payment alerts (TableConnectPaymentAlertsService). Each service
 * keeps its own, so a burst of print failures never pushes a payment alert out
 * of the hold queue, nor the other way round. Moved here from
 * PrintFailedAlertsService unchanged, except that how long a toast stays up is
 * now the alert's own (`toastMs`), where it was the print alerts' 12 s.
 *
 * A LOCKED terminal holds its alerts (the newest `heldMax`, one per key, none
 * past its `maxAgeMs` or the end of the POS session) and raises them at the
 * unlock; so does an alert still on screen when the lock comes down, unless
 * the operator had tapped it away. Waiter and Hybrid terminals lock right
 * after Send, a payment and a Bill, and on idle, and a toast sits under the
 * lock overlay (z-index 9999 against --z-lock 10100) until it expires, unseen.
 * The overlay's z-order is left alone: nothing else on the terminal may show
 * above it.
 */
export class LockAwareAlerts {
  /** Alerts waiting for the unlock, oldest first. */
  private held: LockAwareAlert[] = [];

  /** Alerts raised within their `toastMs` (`at`), with the toast's id when the
   *  toast service gave one: still on screen unless tapped away, so a lock
   *  coming down now would cover them (see `onLocked`). */
  private showing: Array<LockAwareAlert & { at: number; toastId: number | null }> = [];

  /** The lock state, resolved on first use (undefined until then; see `lockState`). */
  private posState: PosStateService | null | undefined = undefined;

  /** The store of the signed-in POS user, as last seen (see `onPosAuth`). */
  private posStoreId: string | null = null;

  constructor(
    private readonly toast: ToastService,
    private readonly zone: NgZone,
    // PosStateService is resolved from here when an alert first needs it, not
    // injected: AppComponent starts the alert services on every surface (KDS,
    // kiosk, displays), and constructing PosStateService runs the POS boot —
    // the stored PIN session, its language, its lock — on all of them.
    private readonly injector: Injector,
    /** How many alerts a locked terminal keeps for its next unlock; the oldest go first. */
    private readonly heldMax: number,
    /** Leads the console line when a held alert cannot be raised. */
    private readonly logTag: string,
  ) {}

  /** Raise an alert now, or hold it for the unlock while the terminal is locked. */
  deliver(key: string, message: string, toastMs: number, maxAgeMs: number): void {
    const alert: LockAwareAlert = { key, message, since: Date.now(), toastMs, maxAgeMs };
    if (this.lockState()?.isLocked) {
      this.hold(alert);
      return;
    }
    this.raise(alert);
  }

  /**
   * PosStateService, subscribed to once so a lock covers and an unlock
   * releases, and a session's end drops what it held (`onPosAuth`); null
   * where it cannot be had, and then alerts are raised as they come.
   * `locked$` and `posAuth$` replay the current state on subscribe, which
   * finds nothing to cover, release or drop yet. Public: the services also
   * read the signed-in user off it.
   */
  lockState(): PosStateService | null {
    if (this.posState !== undefined) return this.posState;
    try {
      this.posState = this.injector.get(PosStateService, null);
    } catch {
      this.posState = null;
    }
    this.posState?.locked$.subscribe(locked =>
      this.zone.run(() => (locked ? this.onLocked() : this.release())));
    this.posState?.posAuth$?.subscribe(auth => this.zone.run(() => this.onPosAuth(auth)));
    return this.posState;
  }

  /**
   * Show an alert now, remembering it for as long as it stays on screen.
   *
   * One toast per key: a repeat while the first is still on screen is not
   * raised again, and its newest text is kept for a lock that comes down over
   * it. (The cloud's kitchen.send.failed arrives once per failed kitchen and,
   * on the wire, without the kitchen, so a round that missed three kitchens
   * stacked three identical toasts on every terminal.) A toast the operator
   * tapped away is gone: a repeat after that shows, or a waiter who re-sent
   * and failed again would see nothing and believe it printed. Recorded only
   * once the toast is up: one that could not be raised does not hold back the
   * next.
   */
  private raise(alert: LockAwareAlert): void {
    const now = Date.now();
    this.showing = this.showing.filter(s => this.isOnScreen(s, now));
    const onScreen = this.showing.find(s => s.key === alert.key);
    if (onScreen) {
      onScreen.message = alert.message;
      return;
    }
    const id: unknown = this.toast.error(alert.message, alert.toastMs);
    this.showing.push({ ...alert, at: now, toastId: typeof id === 'number' ? id : null });
  }

  /**
   * Still on screen: raised less than its `toastMs` ago (a clock turned back
   * counts as gone) and, when the toast service gave an id, not tapped away.
   */
  private isOnScreen(s: { at: number; toastMs: number; toastId: number | null }, now: number): boolean {
    if (!within(s.at, now, s.toastMs)) return false;
    if (s.toastId === null) return true;
    const list: unknown = this.toast.toasts$?.value;
    return !Array.isArray(list) || list.some(t => (t as { id?: unknown })?.id === s.toastId);
  }

  /**
   * Keep an alert for the unlock: one per key, the newest few, none past its
   * age, so a stale one never pushes a live one out of the queue.
   */
  private hold(alert: LockAwareAlert): void {
    const now = Date.now();
    // A repeat of the same key replaces the earlier alert, newest text.
    this.held = this.held.filter(a => a.key !== alert.key && !isPastAge(a, now));
    this.held.push(alert);
    if (this.held.length > this.heldMax) {
      this.held = this.held.slice(this.held.length - this.heldMax);
    }
  }

  /**
   * The lock came down. Alerts raised moments before it are still on screen
   * and now under the overlay: a fast failure lands before the settle
   * screen's lock 1.8 s after a payment, or just before the idle lock. They
   * go back in the queue, to be raised again at the unlock, aged from when
   * they first came in. One the operator had tapped away was read: it stays
   * gone.
   */
  private onLocked(): void {
    const now = Date.now();
    for (const s of this.showing) {
      if (this.isOnScreen(s, now)) {
        this.hold({ key: s.key, message: s.message, since: s.since, toastMs: s.toastMs, maxAgeMs: s.maxAgeMs });
      }
    }
    this.showing = [];
  }

  /** The terminal unlocked: raise what it held, once, except what is past its age. */
  private release(): void {
    const now = Date.now();
    const due = this.held.filter(a => !isPastAge(a, now));
    this.held = [];
    for (const alert of due) {
      try {
        this.raise(alert);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(`${this.logTag} held alert could not be raised`, err);
      }
    }
  }

  /**
   * The POS user changed. Held alerts belong to the POS session they came
   * in: when it ends (a session close or an exit leaves nobody signed in)
   * or the store changes, they are dropped, so the next session's first
   * PIN does not raise them. A lock keeps the user signed in, so an unlock
   * still raises what the lock held.
   */
  private onPosAuth(auth: { storeId?: unknown } | null | undefined): void {
    const storeId = typeof auth?.storeId === 'string' ? auth.storeId : null;
    if (!auth || (this.posStoreId !== null && storeId !== this.posStoreId)) {
      this.held = [];
    }
    this.posStoreId = auth ? storeId : null;
  }
}

/**
 * Whether `since` is less than `limitMs` before `now`. A negative age (the
 * clock was turned back) counts as past the limit, so nothing is kept, or
 * kept on screen, for ever.
 */
export function within(since: number, now: number, limitMs: number): boolean {
  const age = now - since;
  return age >= 0 && age < limitMs;
}

/** Whether a held alert is too old to raise. */
function isPastAge(alert: LockAwareAlert, now: number): boolean {
  return !within(alert.since, now, alert.maxAgeMs);
}
